// NotificationsCatalogView — Admin global vê todos os tipos de notificação
// cadastrados, com toggle on/off, JSON exemplo, descrição do trigger e
// últimos N envios.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  Clock,
  Code,
  ExternalLink,
  Loader2,
  PowerOff,
  Save,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  api,
  ApiError,
  type NotificationHistoryRow,
  type NotificationTypeRow
} from "./api";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const list = signal<NotificationTypeRow[]>([]);
const summary = signal<Map<string, { count: number; lastAt: string | null }>>(
  new Map()
);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const openedKey = signal<string | null>(null);

async function load(): Promise<void> {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const [types, sum] = await Promise.all([
      api.listNotificationTypes(),
      api.notificationTypesSummary()
    ]);
    list.value = types.types;
    const m = new Map<string, { count: number; lastAt: string | null }>();
    for (const s of sum.summary) {
      m.set(s.kind, { count: s.count, lastAt: s.lastAt });
    }
    summary.value = m;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      errMsg.value = "Acesso restrito ao admin global.";
    } else {
      errMsg.value = friendlyError(e);
    }
    phase.value = "error";
  }
}

export function NotificationsCatalogView() {
  useEffect(() => {
    void load();
  }, []);

  return (
    <div class="space-y-5">
      <header class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <h1 class="flex items-center gap-2 text-xl font-bold">
            <Bell className="size-5 text-violet-600 dark:text-violet-400" />
            Notificações & Mensagens
          </h1>
          <p class="mt-1 max-w-2xl text-sm text-muted-foreground">
            Catálogo de todos os tipos de notificação que o sistema dispara.
            Use o toggle pra ativar ou desativar o envio sem deploy. Clique pra
            ver últimos envios e payload de exemplo.
          </p>
        </div>
      </header>

      {phase.value === "loading" && (
        <div class="space-y-2">
          <Skeleton class="h-20 w-full" />
          <Skeleton class="h-20 w-full" />
          <Skeleton class="h-20 w-full" />
        </div>
      )}

      {phase.value === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
          <AlertCircle className="mr-2 inline size-4 text-destructive" />
          {errMsg.value}
        </div>
      )}

      {phase.value === "ready" && (
        <ul class="space-y-2">
          {list.value.map((t) => (
            <TypeRow
              key={t.key}
              type={t}
              stats={summary.value.get(t.key)}
              onClick={() => (openedKey.value = t.key)}
              onToggle={async () => {
                try {
                  await api.updateNotificationType(t.key, { isEnabled: !t.isEnabled });
                  await load();
                } catch (e) {
                  void openAlert({
                    title: "Erro",
                    message: friendlyError(e)
                  });
                }
              }}
            />
          ))}
        </ul>
      )}

      {openedKey.value && (
        <DetailDrawer
          typeKey={openedKey.value}
          onClose={() => (openedKey.value = null)}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}

function TypeRow({
  type,
  stats,
  onClick,
  onToggle
}: {
  type: NotificationTypeRow;
  stats?: { count: number; lastAt: string | null };
  onClick: () => void;
  onToggle: () => void;
}) {
  return (
    <li
      class={`group flex items-stretch gap-1 rounded-md border bg-card transition-colors hover:border-violet-500/40 hover:bg-violet-500/5 ${
        type.isEnabled ? "border-border" : "border-muted bg-muted/30 opacity-70"
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        class="flex min-w-0 flex-1 items-start gap-3 px-4 py-3 text-left"
      >
        <div
          class={`grid size-9 shrink-0 place-items-center rounded-md ${
            type.isEnabled
              ? "bg-violet-500/15 text-violet-600 dark:text-violet-400"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {type.isEnabled ? <Bell className="size-4" /> : <PowerOff className="size-4" />}
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-baseline gap-2">
            <span class="truncate font-semibold">{type.label}</span>
            <code class="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
              {type.key}
            </code>
            {!type.isEnabled && (
              <span class="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Desativado
              </span>
            )}
          </div>
          {type.description && (
            <p class="mt-0.5 line-clamp-1 text-[12px] text-muted-foreground">
              {type.description}
            </p>
          )}
          <div class="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            <span class="inline-flex items-center gap-1">
              <Clock className="size-3" />
              {stats?.count ?? 0} envios nos últimos 7d
            </span>
            {stats?.lastAt && (
              <>
                <span class="opacity-50">·</span>
                <span>último em {fmtDate(stats.lastAt)}</span>
              </>
            )}
            <span class="opacity-50">·</span>
            <span>canais: {(type.channels ?? []).join(", ")}</span>
          </div>
        </div>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        title={
          type.isEnabled
            ? "Desativar — pausa o envio desse tipo"
            : "Ativar — volta a enviar esse tipo"
        }
        aria-label={`${type.isEnabled ? "Desativar" : "Ativar"} ${type.label}`}
        class={`m-2 inline-flex shrink-0 items-center gap-1 self-center rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
          type.isEnabled
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-300"
            : "border-border bg-background text-muted-foreground hover:bg-accent"
        }`}
      >
        {type.isEnabled ? (
          <>
            <CheckCircle2 className="size-3.5" />
            Ativo
          </>
        ) : (
          <>
            <PowerOff className="size-3.5" />
            Inativo
          </>
        )}
      </button>
    </li>
  );
}

function DetailDrawer({
  typeKey,
  onClose,
  onChanged
}: {
  typeKey: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const type = list.value.find((t) => t.key === typeKey);
  const [history, setHistory] = useState<NotificationHistoryRow[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descDraft, setDescDraft] = useState(type?.description ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingHistory(true);
    void api
      .notificationTypeHistory(typeKey)
      .then((r) => {
        if (!cancelled) {
          setHistory(r.history);
          setLoadingHistory(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [typeKey]);

  if (!type) return null;

  async function saveDescription() {
    setSaving(true);
    try {
      await api.updateNotificationType(typeKey, { description: descDraft });
      setEditingDescription(false);
      onChanged();
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-40 flex bg-black/40 backdrop-blur-sm"
      /* Modal admin não fecha por click no backdrop. [[feedback-modal-no-click-outside-close]] */
    >
      <div class="ml-auto flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l border-border bg-card shadow-2xl">
        <header class="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border bg-card/95 px-5 py-4 backdrop-blur">
          <div class="min-w-0">
            <h2 class="flex items-center gap-2 text-base font-bold">
              <Bell className="size-4 text-violet-600" />
              {type.label}
            </h2>
            <code class="text-[11px] text-muted-foreground">{type.key}</code>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="space-y-5 px-5 py-5">
          {/* Descrição (editável) */}
          <section>
            <div class="mb-1 flex items-center justify-between gap-2">
              <h3 class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Descrição
              </h3>
              {!editingDescription && (
                <button
                  type="button"
                  onClick={() => {
                    setDescDraft(type.description ?? "");
                    setEditingDescription(true);
                  }}
                  class="text-[11px] text-muted-foreground hover:text-foreground"
                >
                  Editar
                </button>
              )}
            </div>
            {editingDescription ? (
              <div class="space-y-2">
                <textarea
                  value={descDraft}
                  onInput={(e) =>
                    setDescDraft((e.target as HTMLTextAreaElement).value)
                  }
                  rows={3}
                  class="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
                <div class="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void saveDescription()}
                    disabled={saving}
                    class="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <Save className="size-3.5" />
                    )}
                    Salvar
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingDescription(false)}
                    class="rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-accent"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <p class="text-sm text-foreground">
                {type.description || (
                  <span class="italic text-muted-foreground">Sem descrição.</span>
                )}
              </p>
            )}
          </section>

          {/* Trigger */}
          <section>
            <h3 class="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Quando é disparada
            </h3>
            <p class="rounded-md border border-blue-500/30 bg-blue-500/5 px-3 py-2 text-[12px] text-blue-700 dark:text-blue-300">
              {type.triggerDescription || "Trigger não documentado."}
            </p>
          </section>

          {/* Canais */}
          <section>
            <h3 class="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Canais habilitados
            </h3>
            <div class="flex flex-wrap gap-2">
              {(type.channels ?? []).map((c) => (
                <span
                  key={c}
                  class="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium"
                >
                  {c}
                </span>
              ))}
            </div>
          </section>

          {/* Payload exemplo */}
          {type.examplePayload && (
            <section>
              <h3 class="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <Code className="size-3" />
                Payload de exemplo
              </h3>
              <pre class="overflow-x-auto rounded-md border border-border bg-muted/30 p-3 text-[11px] leading-relaxed text-foreground">
                {JSON.stringify(type.examplePayload, null, 2)}
              </pre>
            </section>
          )}

          {/* Histórico */}
          <section>
            <h3 class="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Últimos envios ({history.length} de até 50)
            </h3>
            {loadingHistory ? (
              <Skeleton class="h-32 w-full" />
            ) : history.length === 0 ? (
              <p class="rounded-md border border-dashed border-border bg-card/40 px-4 py-6 text-center text-sm text-muted-foreground">
                Nenhuma notificação desse tipo nos registros recentes.
              </p>
            ) : (
              <ul class="space-y-1.5">
                {history.map((h) => (
                  <li
                    key={h.id}
                    class="rounded-md border border-border bg-background px-3 py-2"
                  >
                    <div class="flex items-baseline justify-between gap-2">
                      <span class="truncate text-[13px] font-semibold">
                        {h.title}
                      </span>
                      <span class="shrink-0 text-[10px] text-muted-foreground">
                        {fmtDate(h.createdAt)}
                      </span>
                    </div>
                    {h.body && (
                      <p class="mt-0.5 line-clamp-2 text-[12px] text-muted-foreground">
                        {h.body}
                      </p>
                    )}
                    <div class="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
                      <span>user: <code>{h.userId.slice(0, 8)}…</code></span>
                      {h.linkUrl && (
                        <a
                          href={h.linkUrl}
                          target="_blank"
                          rel="noreferrer"
                          class="inline-flex items-center gap-0.5 text-violet-600 hover:underline dark:text-violet-300"
                        >
                          {h.linkUrl}
                          <ExternalLink className="size-2.5" />
                        </a>
                      )}
                      {h.readAt && <span>lida em {fmtDate(h.readAt)}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return iso;
  }
}
